package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.accentGreen
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun UpdateEventScreen(
    clubId: String,
    eventId: String,
    viewModel: UpdateEventViewModel = koinViewModel(parameters = { parametersOf(clubId, eventId) }),
    navigateTo: (UpdateEventViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = { viewModel.fetchEvent() },
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> UpdateEventScreenContent(
                    uiState = state.data,
                    navigateTo = navigateTo,
                    onTitleChanged = viewModel::onTitleChanged,
                    onDescriptionChanged = viewModel::onDescriptionChanged,
                    onLocationChanged = viewModel::onLocationChanged,
                    onDateChanged = viewModel::onDateChanged,
                    onEndDateChanged = viewModel::onEndDateChanged,
                    onStartsAtChanged = viewModel::onStartsAtChanged,
                    onEndsAtChanged = viewModel::onEndsAtChanged,
                    onSubmitClick = viewModel::saveChanges,
                    onDeleteClick = viewModel::deleteEvent,
                    onDeleteDialogDismiss = viewModel::clearDeleteError,
                )
            }
        }
    }
}

@Composable
private fun UpdateEventScreenContent(
    uiState: EventFormUiState,
    navigateTo: (UpdateEventViewModel.NavigationEvent) -> Unit,
    onTitleChanged: (String) -> Unit,
    onDescriptionChanged: (String) -> Unit,
    onLocationChanged: (String) -> Unit,
    onDateChanged: (String) -> Unit,
    onEndDateChanged: (String) -> Unit,
    onStartsAtChanged: (String) -> Unit,
    onEndsAtChanged: (String) -> Unit,
    onSubmitClick: () -> Unit,
    onDeleteClick: () -> Unit,
    onDeleteDialogDismiss: () -> Unit,
) {
    var showDeleteDialog by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        EventFormHeader(
            title = Res.string.edit_existing_event,
            description = Res.string.update_event_page_description,
            icon = Res.drawable.ic_document_edit,
            iconContentDescription = Res.string.edit_event_icon_cd,
            onBackClick = { navigateTo(UpdateEventViewModel.NavigationEvent.Back) },
        )

        Spacer(modifier = Modifier.height(52.dp))

        EventFormSection(
            title = Res.string.edit_details,
            iconContentDescription = Res.string.event_details_icon_cd,
        )

        Spacer(modifier = Modifier.height(28.dp))

        EventFormFields(
            form = uiState,
            onTitleChanged = onTitleChanged,
            onDescriptionChanged = onDescriptionChanged,
            onLocationChanged = onLocationChanged,
            onDateChanged = onDateChanged,
            onEndDateChanged = onEndDateChanged,
            onStartsAtChanged = onStartsAtChanged,
            onEndsAtChanged = onEndsAtChanged,
        )

        Spacer(modifier = Modifier.height(20.dp))

        EventValidationError(error = uiState.validationError)

        Spacer(modifier = Modifier.height(20.dp))

        FormErrorText(error = uiState.formError)

        Spacer(modifier = Modifier.height(28.dp))

        EventSubmitButton(
            text = Res.string.save_changes_button,
            isSubmitting = uiState.isSubmitting,
            onClick = onSubmitClick,
            containerColor = MaterialTheme.colorScheme.accentGreen,
        )

        Spacer(modifier = Modifier.height(16.dp))

        OutlinedButton(
            onClick = {
                onDeleteDialogDismiss()
                showDeleteDialog = true
            },
            enabled = !uiState.isSubmitting && !uiState.isDeleting,
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = MaterialTheme.colorScheme.error,
            ),
        ) {
            Text(
                text = stringResource(Res.string.delete_event_button),
                style = MaterialTheme.typography.titleLarge,
            )
        }

        Spacer(modifier = Modifier.height(36.dp))
    }

    if (showDeleteDialog) {
        DeleteEventConfirmationDialog(
            isDeleting = uiState.isDeleting,
            error = uiState.deleteError,
            onCancel = {
                if (!uiState.isDeleting) {
                    showDeleteDialog = false
                    onDeleteDialogDismiss()
                }
            },
            onDelete = onDeleteClick,
        )
    }
}

@Composable
private fun DeleteEventConfirmationDialog(
    isDeleting: Boolean,
    error: StringResource?,
    onCancel: () -> Unit,
    onDelete: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = {
            if (!isDeleting) {
                onCancel()
            }
        },
        title = {
            Text(stringResource(Res.string.delete_event_dialog_title))
        },
        text = {
            Column {
                Text(stringResource(Res.string.delete_event_dialog_message))
                FormErrorText(error = error)
            }
        },
        confirmButton = {
            TextButton(
                onClick = onDelete,
                enabled = !isDeleting,
                colors = ButtonDefaults.textButtonColors(
                    contentColor = MaterialTheme.colorScheme.error,
                ),
            ) {
                if (isDeleting) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        strokeWidth = 2.dp,
                    )
                } else {
                    Text(stringResource(Res.string.delete_event_confirm))
                }
            }
        },
        dismissButton = {
            TextButton(
                onClick = onCancel,
                enabled = !isDeleting,
            ) {
                Text(stringResource(Res.string.cancel))
            }
        },
    )
}

@Preview(name = "Update Event Screen")
@Composable
private fun UpdateEventScreenPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                UpdateEventScreenContent(
                    uiState = EventFormUiState(
                        title = "Board Game Night",
                        description = "Games, snacks, and community time",
                        location = "Rainbow Club Room",
                        date = "2026-06-23",
                        endDate = "2026-06-23",
                        startsAt = "17:00",
                        endsAt = "20:30",
                    ),
                    navigateTo = {},
                    onTitleChanged = {},
                    onDescriptionChanged = {},
                    onLocationChanged = {},
                    onDateChanged = {},
                    onEndDateChanged = {},
                    onStartsAtChanged = {},
                    onEndsAtChanged = {},
                    onSubmitClick = {},
                    onDeleteClick = {},
                    onDeleteDialogDismiss = {},
                )
            }
        }
    }
}
